/* eslint-disable no-console */
import React, { useState } from "react";
import { Image, Button, Icon, Confirm, Label } from "semantic-ui-react";
import { isSameDay } from "date-fns";
import { BasicModal } from "../../../Shared";
import { buildStorageUrl, formatEventDate } from "../../../../utils";
import { Event } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { EventForm } from "../EventForm";
import "./EventListItem.scss";

const eventController = new Event();

export function EventListItem(props) {
  const { event, onReload } = props;
  const [showModal, setShowModal] = useState(false);
  const [titleModal, setTitleModal] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const { accessToken } = useAuth();

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  const openUpdateEvent = () => {
    setTitleModal(`Actualizar: ${event.title}`);
    onOpenCloseModal();
  };

  const onDelete = async () => {
    try {
      await eventController.deleteEvent(accessToken, event.id);
      onReload();
      onOpenCloseConfirm();
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "grey",
      published: "green",
      cancelled: "red",
      completed: "blue",
    };
    return colors[status] || "grey";
  };

  const getStatusText = (status) => {
    const texts = {
      draft: "Borrador",
      published: "Publicado",
      cancelled: "Cancelado",
      completed: "Completado",
    };
    return texts[status] || status;
  };

  const getAudienceText = (audience) => {
    const texts = {
      all: "Todos",
      students: "Estudiantes",
      teachers: "Docentes",
      parents: "Padres",
      public: "Público",
    };
    return texts[audience] || audience;
  };

  const formatDate = (date) => {
    try {
      return formatEventDate(date, "dd MMM yyyy, hh:mm a");
    } catch {
      return "Fecha no disponible";
    }
  };

  const formatDateRange = () => {
    try {
      const startDate = new Date(event.eventDate);
      const endDate = event.endDate ? new Date(event.endDate) : null;

      // Si no hay fecha final o son el mismo día, mostrar solo una fecha
      if (!endDate || isSameDay(startDate, endDate)) {
        return formatEventDate(startDate, "dd MMM yyyy, hh:mm a");
      }

      // Si son diferentes, mostrar el rango
      const startFormatted = formatEventDate(startDate, "dd MMM yyyy");
      const endFormatted = formatEventDate(endDate, "dd MMM yyyy");

      return `${startFormatted} - ${endFormatted}`;
    } catch {
      return "Fecha no disponible";
    }
  };

  const getImageUrl = () => {
    if (event.imageUrl) {
      return event.imageUrl.startsWith("http")
        ? event.imageUrl
        : buildStorageUrl(event.imageUrl);
    }
    return null;
  };

  return (
    <>
      <div className="event-list-item">
        <div className="event-list-item__image">
          {getImageUrl() ? (
            <Image src={getImageUrl()} />
          ) : (
            <div className="event-list-item__image-placeholder">
              <Icon name="calendar alternate outline" size="big" />
            </div>
          )}
          {event.featured && (
            <div className="event-list-item__featured">
              <Icon name="star" />
            </div>
          )}
          {event.isVirtual && (
            <div className="event-list-item__virtual">
              <Icon name="video" /> Virtual
            </div>
          )}
        </div>

        <div className="event-list-item__content">
          <div className="event-list-item__header">
            <Label color={getStatusColor(event.status)} size="tiny">
              {getStatusText(event.status)}
            </Label>
            {event.category && (
              <span className="event-list-item__category">
                <Icon name="tag" />
                {event.category.name}
              </span>
            )}
          </div>

          <h3 className="event-list-item__title">{event.title}</h3>

          <p className="event-list-item__description">{event.description}</p>

          <div className="event-list-item__meta">
            {/* <span className="event-list-item__meta-item">
              <Icon name="calendar outline" />
              {formatDate(event.eventDate)}
            </span> */}
            <span className="event-list-item__meta-item">
              <Icon name="calendar outline" />
              {formatDateRange()}
            </span>
            {event.location && !event.isVirtual && (
              <span className="event-list-item__meta-item">
                <Icon name="map marker alternate" />
                {event.location}
              </span>
            )}
            <span className="event-list-item__meta-item">
              <Icon name="users" />
              {getAudienceText(event.targetAudience)}
            </span>
          </div>
        </div>

        <div className="event-list-item__actions">
          {event.slug && (
            <Button
              color="grey"
              href={`/events/${event.slug}`}
              target="_blank"
              className="event-list-item__btn event-list-item__btn--view"
            >
              <Icon name="eye" />
              <span>Ver</span>
            </Button>
          )}
          <Button
            primary
            onClick={openUpdateEvent}
            className="event-list-item__btn"
          >
            <Icon name="pencil" />
            <span>Editar</span>
          </Button>
          <Button
            color="red"
            onClick={onOpenCloseConfirm}
            className="event-list-item__btn"
          >
            <Icon name="trash" />
            <span>Eliminar</span>
          </Button>
        </div>
      </div>

      <BasicModal
        show={showModal}
        close={onOpenCloseModal}
        title={titleModal}
        size="large"
        scrolling
      >
        <EventForm
          onClose={onOpenCloseModal}
          onReload={onReload}
          event={event}
        />
      </BasicModal>

      <Confirm
        confirmButton="Eliminar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={onDelete}
        content={`¿Está seguro de eliminar el evento "${event.title}"?`}
        size="mini"
      />
    </>
  );
}
